import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ChatPanel } from "@/components/meridian/ChatPanel";
import LoanCanvas from "@/components/loan/LoanCanvas";
import WireCanvas from "@/components/wire/WireCanvas";
import ComplianceCanvas from "@/components/compliance/ComplianceCanvas";
import KycCanvas from "@/components/kyc/KycCanvas";

const SUGGEST = {
  loan: ["Attach the sample documents and extract the key facts", "Why did the system recommend this maximum LTV, and what would change the recommendation?", "Which market assumptions drive the 7-year forecast?", "Generate the credit memo"],
  compliance: ["Run the gap analysis for Mexico", "Compare US and UK suspicious activity reporting deadlines", "Switch to the EU and summarize the biggest gaps", "Which gaps are still pending human review?"],
  custom: ["Summarize what is missing or uncertain in this loan file", "Why did the system recommend this amount, and what would change it?", "Which figures came from scanned pages?", "Generate the credit memo"],
  kyc: ["Why is Brightwater Remittance Corp rated High risk?", "What is blocking approval of Thornfield Aggregates LLC?", "Summarize the open OFAC matches across all cases", "Which cases are ready for sign-off?"],
  wire: ["Show wires over $1M from Bank of Texas this week that need AML review", "Only show AML holds", "Show outgoing wires to non-US countries", "Explain alert AML-2026-0412"],
};

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refreshTasks } = useApp();
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [pendingMemo, setPendingMemo] = useState(false);
  const [openAlert, setOpenAlert] = useState(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tasks/${id}`);
    setTask(data);
    setVersion((v) => v + 1);
    refreshTasks();
  }, [id, refreshTasks]);
  useEffect(() => { setTask(null); setTab(new URLSearchParams(window.location.search).get("tab") || "dashboard"); load().catch(() => nav("/")); }, [id, load, nav]);

  useEffect(() => {
    const h = (e) => { if (e.detail.tab) setTab(e.detail.tab); if (e.detail.memo) setPendingMemo(true); if (e.detail.reload) load(); };
    window.addEventListener("cloudscore:workspace", h);
    return () => window.removeEventListener("cloudscore:workspace", h);
  }, [load]);

  const onAction = (ev) => {
    const a = ev.action;
    if (a === "create_loan_tasks") refreshTasks();
    if (a === "open_wire_ops") nav("/task/task-wire-ops");
    if (["attach_samples", "extract", "filter", "clear_filter"].includes(a)) { load(); setTab("dashboard"); }
    if (a === "generate_memo") { setTab("draft"); setPendingMemo(true); }
    if (a === "open_alert" && ev.alert_id) { setTab("alerts"); setOpenAlert(ev.alert_id); }
    if (a === "set_jurisdiction") { load(); setTab("dashboard"); }
    if (a === "run_gap_analysis") { load(); setTab("gaps"); }
  };

  if (!task) return <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Loading task…</div>;
  return (
    <div className="flex flex-1 min-w-0" data-testid="workspace-page">
      <div className="w-[380px] shrink-0 border-r border-slate-200 min-h-0 hidden md:block">
        <ChatPanel key={task.id} taskId={task.id} title={task.title} suggestions={SUGGEST[task.custom ? "custom" : task.type] || []} metrics={task.metrics}
          onAction={onAction} onDone={() => { load(); refreshTasks(); }} />
      </div>
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {task.type === "loan" && <LoanCanvas task={task} tab={tab} setTab={setTab} reload={load} pendingMemo={pendingMemo} onMemoStarted={() => setPendingMemo(false)} version={version} />}
        {task.type === "wire" && <WireCanvas task={task} tab={tab} setTab={setTab} reload={load} openAlert={openAlert} setOpenAlert={setOpenAlert} version={version} />}
        {task.type === "compliance" && <ComplianceCanvas task={task} tab={tab} setTab={setTab} reload={load} version={version} />}
        {task.type === "kyc" && <KycCanvas task={task} tab={tab} setTab={setTab} version={version} />}
        {task.type === "assistant" && <div className="p-8 text-sm text-slate-600">Use Home to talk to the assistant.</div>}
      </div>
    </div>
  );
}
